import apiClient from '../api/apiClient';
import { getImageUrl } from '../../utils/imageUtils';

// =====================================================
// TYPES
// =====================================================

export interface BlogCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  color?: string;
  displayOrder: number;
  isActive: boolean;
}

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  contentHtml?: string;
  featuredImage?: string;
  featuredImagePosition?: string;
  categoryId: number;
  categoryName: string;
  categorySlug: string;
  authorId: number;
  authorName: string;
  primaryAssetSymbol?: string;
  primaryAssetType?: 'STOCK' | 'CRYPTOCURRENCY';
  primaryAssetLogo?: string;
  primaryAssetName?: string;
  additionalAssets?: string[];
  status: 'draft' | 'published' | 'scheduled' | 'archived';
  requiredTiers?: Array<'free' | 'bitcoin-abo'>;
  isFeatured: boolean;
  isPinned: boolean;
  viewCount: number;
  scheduledAt?: string;
  publishedAt?: string;
  metaTitle?: string;
  metaDescription?: string;
  readingTimeMinutes: number;
  version: number;
  createdAt: string;
  updatedAt: string;
  // Access control
  locked?: boolean;
  // User interaction status
  isFavorited?: boolean;
  isRead?: boolean;
  // Price zone status (for related posts)
  hasPriceZones?: boolean;
  hasPriceZoneChanges?: boolean;
  priceZoneCount?: number;
  priceZoneStatus?: 'active' | 'hit' | 'missed' | 'inactive' | 'none';
  // Publication price (asset price when post was published)
  publicationPrice?: number;      // USD Preis
  publicationPriceEur?: number;   // EUR Preis
  publicationPriceCurrency?: string;
  publicationPriceCapturedAt?: string;
  // Current prices for initial posts (from stock_prices table)
  currentPriceUsd?: number;
  currentPriceEur?: number;
  currentPrices?: Record<string, number>; // Alle verfügbaren Währungen { "USD": 123.45, "EUR": 112.30 }
  // X/Twitter Auto-Post
  xAutoPost?: boolean;
  xTweetId?: string;
  xTweetedAt?: string;
  xTweetText?: string;
}

export interface LimitOrder {
  price: number;
  label?: string;
  status?: 'active' | 'hit' | 'missed';
}

export interface BlogPriceZone {
  id: number;
  analysisId: number | null;  // Optional - zones now belong to assets, not analyses
  assetSymbol: string;        // Asset this zone belongs to
  assetType: 'STOCK' | 'CRYPTOCURRENCY';
  zoneType: 'entry' | 'exit';
  // Legacy single-currency fields (werden nicht mehr verwendet)
  priceTop: number;      // Upper boundary
  priceBottom: number;   // Lower boundary
  limitOrderCount: number;
  limitOrders: LimitOrder[];
  // Dual-Currency Felder (USD)
  priceTopUsd?: number;
  priceBottomUsd?: number;
  limitOrdersUsd?: LimitOrder[];
  // Dual-Currency Felder (EUR)
  priceTopEur?: number;
  priceBottomEur?: number;
  limitOrdersEur?: LimitOrder[];
  label?: string;
  color?: string;
  displayOrder: number;
  status: 'active' | 'inactive' | 'archived';  // Aktivitätsstatus
  outcome: 'hit' | 'missed' | null;            // Ergebnis (getroffen/verfehlt)
  isActive: boolean;
  hitAt?: string;
  missedAt?: string;
  archivedAt?: string;
  changeHistory?: any[];
  createdAt: string;
  updatedAt: string;
}

export interface BlogAnalysis {
  id: number;
  postId: number;
  assetSymbol: string;
  assetType: 'STOCK' | 'CRYPTOCURRENCY';
  analysisType: string;
  chartConfig?: any;
  // Dual-Currency Chart-Bilder
  chartImageUsd?: string;
  chartImageEur?: string;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  priceZones?: BlogPriceZone[];
}

export interface PriceZoneChange {
  id: number;
  zoneId: number | null;
  changeType: 'created' | 'price_updated' | 'status_changed' | 'deleted' | 'limit_orders_updated';
  oldValues: any | null;
  newValues: any | null;
  changeDescription: string;
  changedAt: string;
  zoneType?: 'entry' | 'exit';
  zoneLabel?: string;
  assetSymbol?: string;
}


export interface AssetPackage {
  id: number;
  packageName: string;
  packageSlug: string;
  description: string;
  assetSymbol: string;
  assetType: 'STOCK' | 'CRYPTOCURRENCY';
  priceMonthly: number;
  priceYearly: number;
  maxTrackedAssets: number;
  hasYoutubeVideos: boolean;
  hasPriceZones: boolean;
  hasAnalyses: boolean;
  isActive: boolean;
}

export interface UserPackage {
  id: number;
  userId: number;
  packageId: number;
  isActive: boolean;
  billingCycle: 'monthly' | 'yearly';
  subscribedAt: string;
  expiresAt?: string;
  package: AssetPackage;
}

// =====================================================
// BLOG POSTS
// =====================================================

// Transform posts to include full image URLs
const transformPosts = (posts: BlogPost[]): BlogPost[] => {
  return posts.map(post => ({
    ...post,
    featuredImage: post.featuredImage ? getImageUrl(post.featuredImage) : undefined
  }));
};

export const getBlogCategories = async (): Promise<BlogCategory[]> => {
  const response = await apiClient.get('/blog/categories');
  return response.data.categories;
};

export const getBlogPosts = async (params?: {
  categoryId?: number;
  assetSymbol?: string;
  search?: string;
  featured?: boolean;
  favoritesOnly?: boolean;
limit?: number;
  offset?: number;
}): Promise<{ posts: BlogPost[]; pagination: any; userTier: string }> => {
  const response = await apiClient.get('/blog/posts', { params });
  return {
    ...response.data,
    posts: transformPosts(response.data.posts)
  };
};

export const getBlogPostBySlug = async (slug: string): Promise<{
  post: BlogPost;
  analyses: BlogAnalysis[];
}> => {
  const response = await apiClient.get(`/blog/posts/${slug}`);
  return {
    post: {
      ...response.data.post,
      featuredImage: response.data.post.featuredImage
        ? getImageUrl(response.data.post.featuredImage)
        : undefined
    },
    analyses: response.data.analyses || []
  };
};

/**
 * Lazy Loading: YouTube-Videos für einen Blog-Post abrufen
 * Wird separat geladen um die initiale Ladezeit zu verbessern
 */
export interface AiScenario {
  type: 'bullish' | 'bearish' | 'neutral';
  description: string;
  start_seconds: number;
  end_seconds: number;
  price_target?: number;
  price_current?: number;
  price_invalidation?: number;
  currency?: string;
  timeframe?: 'short_term' | 'medium_term' | 'long_term';
  probability?: 'primary' | 'alternative';
}

export interface AiKeyLevel {
  price: number;
  currency: string;
  label: string;
  type: 'resistance' | 'support' | 'target' | 'fibonacci' | 'moving_average' | 'current_price';
}

export interface AiAsset {
  name: string;
  symbol: string;
  mentioned_as?: string[];
  reasoning: string;
  scenarios: AiScenario[];
  key_levels: AiKeyLevel[];
}

export interface AiSummary {
  summary: string;
  main_asset?: { name: string; symbol: string } | null;
  assets: AiAsset[];
}

export interface YouTubeVideo {
  videoId: string;
  title: string;
  thumbnail: string;
  channelId?: string;
  channelTitle: string;
  channelThumbnail?: string;
  publishedAt: string;
  duration?: string;
  matchedBy?: string;
  matchedTerm?: string;
  aiSummary?: AiSummary | null;
}

export interface VideosPagination {
  limit: number;
  offset: number;
  hasMore: boolean;
  total?: number;  // Gesamtanzahl (nur beim ersten Request)
}

export const getPostVideos = async (
  slug: string,
  limit = 20,
  offset = 0
): Promise<{
  videos: YouTubeVideo[];
  pagination: VideosPagination;
}> => {
  const response = await apiClient.get(`/blog/posts/${slug}/videos`, {
    params: { limit, offset }
  });
  return response.data;
};

/**
 * Prüft ob ein Video zu einem abonnierten Kanal gehört (auch pausiert).
 * Gibt Video-Metadaten zurück wenn ja, null wenn nicht.
 */
export const resolvePostVideo = async (
  slug: string,
  videoId: string
): Promise<YouTubeVideo | null> => {
  try {
    const response = await apiClient.get(`/blog/posts/${slug}/videos/${videoId}/resolve`);
    return response.data as YouTubeVideo;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
};

export const getFeaturedPosts = async (): Promise<{
  featuredPosts: BlogPost[];
  userTier: string;
}> => {
  const response = await apiClient.get('/blog/featured');
  return {
    ...response.data,
    featuredPosts: transformPosts(response.data.featuredPosts)
  };
};

export const searchBlogPosts = async (
  query: string,
  limit = 20,
  offset = 0
): Promise<{
  results: BlogPost[];
  query: string;
  pagination: any;
}> => {
  const response = await apiClient.get('/blog/search', {
    params: { q: query, limit, offset }
  });
  return {
    ...response.data,
    results: transformPosts(response.data.results)
  };
};

export const getCategoryBySlug = async (slug: string): Promise<{
  category: BlogCategory;
  posts: BlogPost[];
  userTier: string;
}> => {
  const response = await apiClient.get(`/blog/category/${slug}`);
  return {
    ...response.data,
    posts: transformPosts(response.data.posts)
  };
};

export const getPostsByAsset = async (
  symbol: string,
  limit = 20,
  offset = 0
): Promise<{
  assetSymbol: string;
  posts: BlogPost[];
  pagination: any;
}> => {
  const response = await apiClient.get(`/blog/asset/${symbol}`, {
    params: { limit, offset }
  });
  return {
    ...response.data,
    posts: transformPosts(response.data.posts)
  };
};

export const getPostBySlug = async (slug: string): Promise<BlogPost> => {
  const response = await apiClient.get(`/blog/posts/${slug}`);
  const post = response.data.post;
  return {
    ...post,
    featuredImage: post.featuredImage ? getImageUrl(post.featuredImage) : undefined
  };
};

export const getRelatedPosts = async (postId: number | string, assetSymbol?: string): Promise<BlogPost[]> => {
  // OPTIMIERUNG: Symbol übergeben um eine Backend-Query zu sparen
  const params = assetSymbol ? { symbol: assetSymbol } : {};
  const response = await apiClient.get(`/blog/posts/${postId}/related`, { params });
  return transformPosts(response.data.relatedPosts);
};

/**
 * Get price zone changes announced in a specific post
 * These are changes that should be displayed to users
 */
export const getPostPriceZoneChanges = async (postId: number): Promise<PriceZoneChange[]> => {
  const response = await apiClient.get(`/blog/posts/${postId}/price-zone-changes`);
  return response.data.changes || [];
};

/**
 * Get ALL price zones for an asset across all published posts
 * This allows displaying all zones regardless of which post is being viewed
 */
export const getAllPriceZonesForAsset = async (symbol: string): Promise<BlogPriceZone[]> => {
  const response = await apiClient.get(`/blog/asset/${encodeURIComponent(symbol)}/price-zones`);
  return response.data.priceZones || [];
};


// =====================================================
// ASSET PACKAGES
// =====================================================

export const getAllPackages = async (): Promise<{ packages: AssetPackage[] }> => {
  const response = await apiClient.get('/blog/packages/all');
  return response.data;
};

export const getUserPackages = async (): Promise<{ packages: UserPackage[] }> => {
  const response = await apiClient.get('/blog/packages/my');
  return response.data;
};

export const getPackageBySlug = async (slug: string): Promise<{ package: AssetPackage }> => {
  const response = await apiClient.get(`/blog/packages/${slug}`);
  return response.data;
};

// =====================================================
// HELPERS
// =====================================================

export const getTierBadgeColor = (tier?: string): string => {
  if (tier?.startsWith('fcms-')) return 'bg-blue-500';
  switch (tier) {
    case 'free':
      return 'bg-gray-500';
    case 'bitcoin-abo':
      return 'bg-amber-500';
    default:
      return 'bg-gray-500';
  }
};

export const getTierLabel = (tier?: string): string => {
  if (tier?.startsWith('fcms-')) return 'FCMS Abo';
  switch (tier) {
    case 'free':
      return 'Kostenlos';
    case 'bitcoin-abo':
      return 'Bitcoin Abo';
    default:
      return 'Kostenlos';
  }
};

// NOTE: formatReadingTime moved to utils/formatUtils.ts
// NOTE: formatPublishedDate moved to utils/dateUtils.ts as formatRelativeDate

// =====================================================
// USER INTERACTIONS
// =====================================================

export const toggleFavorite = async (postId: number): Promise<{ success: boolean; isFavorited: boolean }> => {
  const response = await apiClient.post(`/blog/posts/${postId}/favorite`);
  return response.data;
};

export const markPostAsRead = async (postId: number): Promise<{ success: boolean }> => {
  const response = await apiClient.post(`/blog/posts/${postId}/mark-read`);
  return response.data;
};

export const markAllBlogPostsAsRead = async (): Promise<{ success: boolean; marked: number }> => {
  const response = await apiClient.post('/blog/mark-all-read');
  return response.data;
};

export const getUserPostStatus = async (postId: number): Promise<{ isFavorited: boolean; isRead: boolean }> => {
  const response = await apiClient.get(`/blog/posts/${postId}/user-status`);
  return response.data;
};

// =====================================================
// MEDIA LIBRARY
// =====================================================

export interface MediaItem {
  id: number;
  postId: number | null;
  fileName: string;
  originalFilename: string;
  filePath: string;
  fileType: string;
  fileSize: number;
  originalSize: number;
  mimeType: string;
  width: number | null;
  height: number | null;
  altText: string | null;
  caption: string | null;
  tags: string[];
  category: string;
  isOptimized: boolean;
  usageCount: number;
  lastUsedAt: string | null;
  uploadedBy: number | null;
  createdAt: string;
}

export interface MediaTag {
  id: number;
  name: string;
  color: string;
  usageCount: number;
}

export interface MediaCategory {
  id: number;
  name: string;
  slug: string;
  icon: string;
}

export interface MediaFilter {
  search?: string;
  tags?: string[];
  category?: string;
  fileType?: string;
  sortBy?: 'newest' | 'oldest' | 'name' | 'size' | 'usage';
  page?: number;
  limit?: number;
}

export interface MediaStats {
  totalItems: number;
  totalSize: number;
  savedSpace: number;
  byCategory: { category: string; count: number }[];
  byType: { type: string; count: number }[];
}

// NOTE: URL helpers moved to utils/imageUtils.ts
// Use: import { getImageUrl, getMediaThumbnailUrl } from '../../utils/imageUtils';

export const getMedia = async (filter: MediaFilter = {}): Promise<{
  items: MediaItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}> => {
  const params: any = { ...filter };
  if (filter.tags && filter.tags.length > 0) {
    params.tags = filter.tags.join(',');
  }
  const response = await apiClient.get('/admin/blog/media', { params });
  return response.data;
};

export const getMediaById = async (id: number): Promise<MediaItem> => {
  const response = await apiClient.get(`/admin/blog/media/${id}`);
  return response.data.media;
};

export const uploadMedia = async (
  file: File,
  options: {
    tags?: string[];
    category?: string;
    altText?: string;
    caption?: string;
    customFilename?: string;
  } = {}
): Promise<MediaItem> => {
  const formData = new FormData();
  formData.append('file', file);
  if (options.tags) {
    formData.append('tags', JSON.stringify(options.tags));
  }
  if (options.category) {
    formData.append('category', options.category);
  }
  if (options.altText) {
    formData.append('altText', options.altText);
  }
  if (options.caption) {
    formData.append('caption', options.caption);
  }
  if (options.customFilename) {
    formData.append('customFilename', options.customFilename);
  }

  const response = await apiClient.post('/admin/blog/media/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data.media;
};

export const updateMedia = async (
  id: number,
  updates: {
    altText?: string;
    caption?: string;
    tags?: string[];
    category?: string;
  }
): Promise<MediaItem> => {
  const response = await apiClient.put(`/admin/blog/media/${id}`, updates);
  return response.data.media;
};

export const deleteMedia = async (id: number): Promise<boolean> => {
  const response = await apiClient.delete(`/admin/blog/media/${id}`);
  return response.data.success;
};

export const trackMediaUsage = async (id: number): Promise<void> => {
  await apiClient.post(`/admin/blog/media/${id}/track-usage`);
};

export const getMediaTags = async (): Promise<MediaTag[]> => {
  const response = await apiClient.get('/admin/blog/media/tags');
  return response.data.tags;
};

export const createMediaTag = async (name: string, color?: string): Promise<MediaTag> => {
  const response = await apiClient.post('/admin/blog/media/tags', { name, color });
  return response.data.tag;
};

export const updateMediaTag = async (id: number, updates: { name?: string; color?: string }): Promise<MediaTag> => {
  const response = await apiClient.put(`/admin/blog/media/tags/${id}`, updates);
  return response.data.tag;
};

export const deleteMediaTag = async (id: number): Promise<boolean> => {
  const response = await apiClient.delete(`/admin/blog/media/tags/${id}`);
  return response.data.success;
};

export const getMediaCategories = async (): Promise<MediaCategory[]> => {
  const response = await apiClient.get('/admin/blog/media/categories');
  return response.data.categories;
};

export const createMediaCategory = async (data: { name: string; slug: string; description?: string; icon?: string }): Promise<MediaCategory> => {
  const response = await apiClient.post('/admin/blog/media/categories', data);
  return response.data.category;
};

export const updateMediaCategory = async (id: number, updates: { name?: string; slug?: string; description?: string; icon?: string }): Promise<MediaCategory> => {
  const response = await apiClient.put(`/admin/blog/media/categories/${id}`, updates);
  return response.data.category;
};

export const deleteMediaCategory = async (id: number): Promise<boolean> => {
  const response = await apiClient.delete(`/admin/blog/media/categories/${id}`);
  return response.data.success;
};

export const getMediaStats = async (): Promise<MediaStats> => {
  const response = await apiClient.get('/admin/blog/media/stats');
  return response.data.stats;
};

// =====================================================
// DASHBOARD: UNREAD POSTS
// =====================================================

export interface UnreadBlogData {
  unreadCount: number;
  recentUnread: Array<{
    id: string;
    title: string;
    slug: string;
    publishedAt: string;
    primaryAssetSymbol?: string;
    featuredImage?: string;
  }>;
}

/**
 * Anzahl und letzte ungelesene Blog-Posts für das Dashboard abrufen
 * Basiert auf dem User-Tier (Blog-Abo)
 */
export const getUnreadBlogPosts = async (): Promise<UnreadBlogData> => {
  const response = await apiClient.get('/blog/unread-count');
  return response.data.data;
};

// =====================================================
// X/TWITTER AUTO-POST
// =====================================================

export interface TweetPreview {
  tweetText: string;
  characterCount: number;
}

export interface TweetResult {
  success: boolean;
  tweetId: string;
  tweetText: string;
  message: string;
}

/**
 * Tweet-Vorschau fuer einen Blog-Post generieren
 */
export const getTweetPreview = async (postId: string | number): Promise<TweetPreview> => {
  const response = await apiClient.get(`/admin/blog/posts/${postId}/tweet-preview`);
  return response.data;
};

/**
 * Manuell einen Tweet fuer einen Blog-Post absenden
 */
export const postTweet = async (postId: string | number): Promise<TweetResult> => {
  const response = await apiClient.post(`/admin/blog/posts/${postId}/tweet`);
  return response.data;
};

// NOTE: formatFileSize moved to utils/formatUtils.ts
